import axiosInstance from './axiosInstance';
import { getCachedTalentDetail, setCachedTalentDetail } from '../utils/talentDetailCache';

/**
 * 배우 목록 (공개)
 * @param {{ gender, minHeight, maxHeight, orderBy, keyword, callpage, perpage }} params
 * @returns {{ content: Array, total: number, totalPages: number }}
 */
export const getTalentList = async (params) => {
  const res = await axiosInstance.get('/api/talent/list', { params });
  return res.data;
};

/**
 * 배우 프로필 상세 (공개) — ID 또는 slug 모두 지원
 * @param {number|string} idOrSlug
 */
export const getTalentDetail = async (idOrSlug, role = null) => {
  // role을 캐시 키에 포함 — 제작사/비로그인은 응답 내용이 다르므로 role별로 캐싱
  const cacheKey = role ? `${idOrSlug}_${role}` : idOrSlug;
  const cached = getCachedTalentDetail(cacheKey);
  if (cached) return cached;
  const isId = /^\d+$/.test(String(idOrSlug));
  const url = isId ? `/api/talent/${idOrSlug}` : `/api/talent/slug/${idOrSlug}`;
  const res = await axiosInstance.get(url);
  setCachedTalentDetail(cacheKey, res.data);
  return res.data;
};

/**
 * 배우 프로필 생성 (온보딩)
 * @param {{ name, gender, birthDate, stageName, heightCm, weightKg, residence, bio, agency }} data
 * @returns {{ id: number }}
 */
export const createTalentProfile = async (data) => {
  const res = await axiosInstance.post('/api/talent/onboarding', data);
  return res.data;
};

/**
 * 배우 프로필 수정
 * @param {{ id, name, stageName, gender, birthDate, heightCm, weightKg, bio,
 *           phone, residence, contactEmail, visibility, instagramUrl, youtubeUrl, extraLink }} data
 */
export const updateTalentProfile = async (data) => {
  await axiosInstance.put('/api/talent', data);
};

/**
 * 공개/비공개 설정
 * @param {"PUBLIC"|"PRIVATE"} visibility
 */
export const setTalentVisibility = async (visibility) => {
  await axiosInstance.patch('/api/talent/profile/visibility', { visibility });
};

/**
 * 배우 미디어 등록 (사진·영상)
 * @param {{ talentProfileId, fileEntityId?, videoUrl?, mediaType, photoType?, isIntroVideo? }} data
 */
export const addTalentMedia = async (data) => {
  const res = await axiosInstance.post('/api/talent/media', data);
  return res.data;
};

/**
 * 대표 작품 등록
 * @param {{ talentProfileId, title, role, year, company, mediaType, videoUrl, sortOrder }} data
 */
export const addTalentWork = async (data) => {
  const res = await axiosInstance.post('/api/talent/works', data);
  return res.data;
};

/**
 * 특기 등록
 * @param {{ talentProfileId, skillTagId: number|null, customSkill: string|null }} data
 */
export const addTalentSkill = async (data) => {
  const res = await axiosInstance.post('/api/talent/skills', data);
  return res.data;
};

/**
 * 배우 프로필 특기 목록 조회
 * @param {number} talentProfileId
 * @returns {{ id: number, name: string }[]}
 */
export const getTalentSkills = async (talentProfileId) => {
  const res = await axiosInstance.get('/api/talent/skills', { params: { talentProfileId } });
  return Array.isArray(res.data) ? res.data : (res.data?.skills ?? []);
};

/**
 * 스킬 태그 목록 조회
 * @returns {{ id: number, name: string, category: string }[]}
 */
export const getSkillTags = async () => {
  const res = await axiosInstance.get('/api/skill-tags');
  return res.data;
};

/**
 * 내 배우 프로필 조회 (로그인 사용자 본인)
 * @returns TalentProfileResDto (id 포함)
 */
export const getMyTalentProfile = async () => {
  const res = await axiosInstance.get('/api/talent/me');
  return res.data;
};

/**
 * 배우 프로필 통계 (공개)
 * @param {number} id - talentProfileId
 * @returns {{ viewCount, bookmarkCount, applicationCount, reviewAvg, reviewCount }}
 */
export const getTalentStats = async (id) => {
  const res = await axiosInstance.get(`/api/talent/${id}/stats`);
  return res.data;
};

/**
 * 내 배우 프로필 통계 (인증 필요)
 * @returns {{ viewCount, bookmarkCount, applicationCount, reviewAvg, reviewCount }}
 */
export const getMyTalentStats = async () => {
  const res = await axiosInstance.get('/api/talent/me/stats');
  return res.data;
};

/**
 * 배우 프로필 조회수 (공개)
 * @param {number} id - talentProfileId
 * @returns {{ totalViews: number, weeklyUniqueViewers: number }}
 */
export const getTalentViews = async (id) => {
  const res = await axiosInstance.get(`/api/talent/${id}/views`);
  return res.data;
};

/**
 * 내 배우 프로필 조회수 (인증 필요)
 * @returns {{ totalViews: number, totalViewers: number, weeklyUniqueViewers: number }}
 */
export const getMyTalentViews = async () => {
  const res = await axiosInstance.get('/api/talent/me/views');
  return res.data;
};

/**
 * 배우 프로필 조회 기록
 * @param {number} id - talentProfileId
 */
export const recordTalentVisit = async (id) => {
  await axiosInstance.post(`/api/talent/${id}/visit`);
};

/**
 * 배우 미디어 사진 목록 조회
 * @param {number} talentProfileId
 * @param {"profile"|"filmography"} [type] - 생략 시 전체
 * @returns {{ photos: { id: number, url: string, photoType: string }[] }}
 */
export const getTalentMediaPhotos = async (talentProfileId, type) => {
  const params = { talentProfileId };
  if (type) params.type = type;
  const res = await axiosInstance.get('/api/talent/media/photos', { params });
  return res.data;
};

/** 대표 작품 수정 */
export const updateTalentWork = async (id, data) => {
  const res = await axiosInstance.put(`/api/talent/works/${id}`, data);
  return res.data;
};

/** 대표 작품 삭제 */
export const deleteTalentWork = async (id) => {
  await axiosInstance.delete(`/api/talent/works/${id}`);
};

/** 미디어 삭제 */
export const deleteTalentMedia = async (id) => {
  await axiosInstance.delete(`/api/talent/media/${id}`);
};

/** 스킬 삭제 */
export const deleteTalentSkill = async (id) => {
  await axiosInstance.delete(`/api/talent/skills/${id}`);
};

/** PDF 레이아웃 불러오기 — 저장된 적 없으면 layoutJson: null */
export const getTalentPdfLayout = async () => {
  const res = await axiosInstance.get('/api/talent/me/pdf-layout');
  return res.data;
};

/** PDF 레이아웃 저장 (upsert) */
export const saveTalentPdfLayout = async (layoutJson) => {
  await axiosInstance.put('/api/talent/me/pdf-layout', { layoutJson });
};

/**
 * 계정 설정 조회 (알림, 검색 노출 등)
 * @returns {{ emailNotification, pushNotification, smsNotification,
 *             castingNotification, messageNotification, marketingNotification,
 *             searchable }}
 */
export const getAccountSettings = async () => {
  const res = await axiosInstance.get('/api/talent/account');
  return res.data;
};

/**
 * 계정 설정 수정 (알림, 검색 노출 등)
 *
 * 보낸 값만 바꾸므로 바뀐 항목만 보내면 된다.
 * 응답은 바뀐 뒤의 설정 전체라, 그대로 화면에 반영하면 서버와 어긋나지 않는다.
 *
 * @param {{ emailNotification?, pushNotification?, smsNotification?,
 *           castingNotification?, messageNotification?, marketingNotification?,
 *           searchable? }} data
 */
export const updateAccountSettings = async (data) => {
  const res = await axiosInstance.patch('/api/talent/account', data);
  return res.data;
};
